import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Tooltip,
  Popover,
  Button
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import EditIcon from '@mui/icons-material/Edit';
import useApi from 'hooks/useApi';
import { useParams } from 'react-router';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { useTheme } from '@mui/material/styles';
import DeleteIcon from '@mui/icons-material/Delete';
type StatusItem = {
  _id: string;
  name: string;
  uid: string;
};

const StatusTableManager = ({ onClose }: { onClose: () => void }) => {
  const theme = useTheme();
  const { teamUid } = useParams();
  const { getNodeStatus, createNodeStatus, updateNodeStatus,deleteNodeStatus } = useApi();

  const [statuses, setStatuses] = useState<StatusItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [initialValues, setInitialValues] = useState({ uid: '', name: '' });
  // Add popover
  const [addAnchorEl, setAddAnchorEl] = useState<null | HTMLElement>(null);
  const [deleteAnchorEl, setDeleteAnchorEl] = useState<null | HTMLElement>(null);
  const [statusToDelete, setStatusToDelete] = useState<StatusItem | null>(null);
  const validationSchema = Yup.object({
    name: Yup.string().required('Status Name is required'),
    uid: Yup.string().optional()
  });

  const extractNumber = (str:any) => {
  const match = str.match(/^\d+/);  // get leading digits
  return match ? Number(match[0]) : Infinity; // if no number, send to bottom
};

  const fetchStatuses = async () => {
    if (!teamUid) return;
    setLoading(true);
    try {
      const res = await getNodeStatus(teamUid);
      const sorted = [...res.data].sort((a:any, b:any) => {
            const numA = extractNumber(a.name);
            const numB = extractNumber(b.name);

            // 1️⃣ First: sort by number (numbers before non-numbers)
            if (numA !== numB) return numA - numB;

            // 2️⃣ Then: if both are same group (both numbered with same num OR both Infinity),
            // sort alphabetically by name
            return a.name.localeCompare(b.name, undefined, { sensitivity: "base" });
          })
      setStatuses(sorted);
    } catch {
      setStatuses([]);
    } finally {
      setLoading(false);
    }
  };
  

  const handleDeleteStatus = async () => {
  if (!statusToDelete || !teamUid) return;
  
  setLoading(true);
  try {
    await deleteNodeStatus(statusToDelete.uid);
    await fetchStatuses(); // Refresh the list
    setDeleteAnchorEl(null);
    setStatusToDelete(null);
  } catch (error: any) {
    console.error('Delete failed', error);
    // You can add error toast/notification here
    alert(error?.response?.data?.message || 'Failed to delete status');
  } finally {
    setLoading(false);
  }
};
  useEffect(() => {
    fetchStatuses();
  }, [teamUid]);

  const submitFormData = async (data: { uid: string; name: string }, setSubmitting: (isSub: boolean) => void) => {
    if (!teamUid) return;
    setSubmitting(true);
    try {
      if (data.uid) {
        await updateNodeStatus({ name: data.name }, data.uid);
      } else {
        await createNodeStatus({ name: data.name }, teamUid);
      }
    } catch (error) {
      console.error('Submission failed', error);
    } finally {
      setSubmitting(false);
      setAddAnchorEl(null);
      onClose();
    }
  };

  return (
    <>
    <Box p={2}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} sx={{ backgroundColor: theme.palette.background.paper }}>
        <Typography variant="h6" fontWeight={600}>
          Manage Reporting Statuses
        </Typography>
        <Tooltip title="Add new status">
          <IconButton
            color="primary"
            onClick={(e) => {
              setAddAnchorEl(e.currentTarget);
              setInitialValues({ uid: '', name: '' });
            }}
            sx={{ color: theme.palette.primary.main }}
          >
            <AddCircleOutlineIcon fontSize="large" />
          </IconButton>
        </Tooltip>
      </Box>

      <TableContainer
        component={Paper}
        sx={{
          '& .MuiTableCell-head': {
            fontWeight: 'bold',
            backgroundColor: theme.palette.background.default
          }
        }}
      >
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell> Status Names</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {statuses.map((status: { name: string; uid: string; _id: string }) => (
              <TableRow
                key={status.uid}
                hover
                sx={{
                  '&:hover': {
                    backgroundColor: theme.palette.action.hover
                  }
                }}
              >
                <TableCell>{status.name}</TableCell>
                <TableCell align="right">
                  <Box display="flex" justifyContent="flex-end" gap={1}>
                    <Tooltip title="Edit status">
                      <IconButton
                        size="medium"
                        color="primary"
                        onClick={(e) => {
                          setAddAnchorEl(e.currentTarget);
                          setInitialValues({ name: status.name, uid: status.uid });
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete status">
                      <IconButton
                        size="medium"
                        color="error"
                        onClick={(e) => {
                          setDeleteAnchorEl(e.currentTarget);
                          setStatusToDelete(status);
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
            {statuses.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={2} align="center">
                  No statuses found.
                </TableCell>
              </TableRow>
            )}
            {loading && (
              <TableRow>
                <TableCell colSpan={2} align="center">
                  Loading...
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Manage Status Popover */}
      <Popover
        open={Boolean(addAnchorEl)}
        anchorEl={addAnchorEl}
        onClose={() => setAddAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={(values, { setSubmitting }) => {
            submitFormData(values, setSubmitting);
          }}
        >
          {({ values, errors, touched, handleChange, handleBlur, handleSubmit, isValid, isSubmitting }) => (
            <Box component={'form'} onSubmit={handleSubmit} p={2} sx={{ width: 400 }}>
              <Typography variant="subtitle1" mb={2}>
                {values.uid ? 'Update' : 'Add'} Status
              </Typography>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography sx={{ minWidth: 100 }}>Status Name</Typography>
                <TextField
                  name="name"
                  fullWidth
                  defaultValue={values.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={Boolean(errors.name && touched.name)}
                />
              </Box>
              <Box mt={2} display="flex" justifyContent="flex-end" gap={1}>
                <Button type="button" onClick={() => setAddAnchorEl(null)}>
                  Cancel
                </Button>
                <Button variant="contained" type="submit" disabled={!isValid || isSubmitting}>
                  {values.uid ? 'Update' : 'Add'}
                </Button>
              </Box>
            </Box>
          )}
        </Formik>
      </Popover>
    </Box>
    {/* Delete Confirmation Popover */}
<Popover
  open={Boolean(deleteAnchorEl)}
  anchorEl={deleteAnchorEl}
  onClose={() => {
    setDeleteAnchorEl(null);
    setStatusToDelete(null);
  }}
  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
>
  <Box p={3} sx={{ width: 350 }}>
    <Typography variant="subtitle1" fontWeight={600} mb={2} sx={{borderBottom:'1px solid #ccc',pb:0.2 }}>
      Delete Status
    </Typography>
    <Typography variant="body2" color="text.secondary" mb={3}>
        Are you sure you want to delete "{statusToDelete?.name}"? This status will be removed from all steps using it.
    </Typography>
    <Box display="flex" justifyContent="flex-end" gap={1}>
      <Button
        onClick={() => {
          setDeleteAnchorEl(null);
          setStatusToDelete(null);
        }}
      >
        Cancel
      </Button>
      <Button
        variant="contained"
        color="error"
        onClick={handleDeleteStatus}
        disabled={loading}
      >
        {loading ? 'Deleting...' : 'Delete'}
      </Button>
    </Box>
  </Box>
</Popover>
    </>
  );
};

export default StatusTableManager;
